import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import api from "../lib/api";
import { Eye, EyeSlash, LockKey } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();

    setError("");

    if (!token) {
      setError("El enlace de recuperación no es válido.");
      return;
    }

    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    if (password !== repeatPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    try {
      setSubmitting(true);

      await api.post("/auth/reset-password-token", {
        token,
        new_password: password,
      });

      setSuccess(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;

      setError(
        typeof detail === "string"
          ? detail
          : "No se pudo cambiar la contraseña.",
      );
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2">
      {/* Imagen lateral, igual que Login */}
      <div
        className="relative hidden lg:block overflow-hidden"
        style={{
          backgroundImage: 'url("/login.png")',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/10 to-med-primary/30" />

        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-xs uppercase tracking-[0.3em] mb-3 opacity-80">
            Fiesta · Tradición · Comunidad
          </div>

          <h2 className="font-display text-4xl xl:text-5xl font-bold leading-tight max-w-md">
            Tu falla, organizada como nunca.
          </h2>
        </div>
      </div>

      {/* Formulario */}
      <div className="flex items-center justify-center p-8 bg-med-bg">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-14 h-14 rounded-3xl bg-med-primary text-white grid place-items-center shadow-lg shadow-med-primary/30">
              <LockKey size={30} weight="duotone" />
            </div>

            <div>
              <div className="font-display text-2xl font-bold text-med-text">
                Trakka
              </div>

              <div className="text-xs uppercase tracking-[0.2em] text-med-muted">
                Recuperación de contraseña
              </div>
            </div>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold text-med-text mb-2">
            Nueva contraseña
          </h1>

          <p className="text-med-muted mb-10">
            Introduce una nueva contraseña para tu cuenta.
          </p>

          {!token ? (
            <div className="text-sm text-med-danger bg-med-danger/10 rounded-xl px-4 py-3">
              El enlace de recuperación no es válido.
            </div>
          ) : success ? (
            <div className="space-y-5">
              <div className="text-sm text-green-700 bg-green-50 rounded-xl px-4 py-4">
                Tu contraseña se ha actualizado correctamente.
              </div>

              <button
                type="button"
                onClick={() =>
                  navigate("/login", {
                    replace: true,
                  })
                }
                className="w-full bg-med-primary hover:bg-med-primary-hover text-white rounded-xl py-3 font-semibold transition-colors"
              >
                Ir a iniciar sesión
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5">
              {/* Nueva contraseña */}
              <div>
                <label className="block text-sm font-medium text-med-text mb-2">
                  Nueva contraseña
                </label>

                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 rounded-xl px-4 py-3 pr-12 outline-none transition-all"
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-med-muted hover:text-med-text transition-colors"
                  >
                    {showPassword ? (
                      <EyeSlash size={20} weight="duotone" />
                    ) : (
                      <Eye size={20} weight="duotone" />
                    )}
                  </button>
                </div>
              </div>

              {/* Repetir contraseña */}
              <div>
                <label className="block text-sm font-medium text-med-text mb-2">
                  Repite la contraseña
                </label>

                <div className="relative">
                  <input
                    type={showRepeatPassword ? "text" : "password"}
                    value={repeatPassword}
                    onChange={(e) => setRepeatPassword(e.target.value)}
                    className="w-full bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 rounded-xl px-4 py-3 pr-12 outline-none transition-all"
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />

                  <button
                    type="button"
                    onClick={() => setShowRepeatPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-med-muted hover:text-med-text transition-colors"
                  >
                    {showRepeatPassword ? (
                      <EyeSlash size={20} weight="duotone" />
                    ) : (
                      <Eye size={20} weight="duotone" />
                    )}
                  </button>
                </div>
              </div>
              {error && (
                <div className="text-sm text-med-danger bg-med-danger/10 rounded-xl px-4 py-3">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-med-primary hover:bg-med-primary-hover text-white rounded-xl py-3 font-semibold transition-colors active:scale-[0.98] disabled:opacity-60"
              >
                {submitting ? "Cambiando contraseña..." : "Cambiar contraseña"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
